import { ActionWithPayload } from "@obg/ngrx-utils";
import { updateState } from "@obg/ngx.global";
import { isEqual } from "lodash-es";

import { omsActionType } from "./oms.action";
import {
	NOTIFICATION_STATUS_DICTIONARY,
	NotificationsInfo,
	NotificationsState,
	NotificationStatusDictionary,
	OmsMessage,
	OmsMessageAction,
	OmsMessageStatus,
	OmsState,
	UpdateMessage,
	UpdateMessagePayload,
	UpdateMessagesPayload,
} from "./oms.model";

const initialState: OmsState = {
	isBusy: false,
	alerts: [],
	userSettings: {},
	notificationsByStatus: {
		active: [],
		expired: [],
	},
	notificationsInfo: {
		unreadNotificationCount: 0,
		unreadProactiveChatCount: 0,
	},
	isAlertRealtimeEnabled: false,
	isNotificationInfoRealtimeEnabled: false,
	alertContext: "",
};

/**
 * @deprecated - [fabric-deprecated]
 */
export function omsReducer(state = initialState, action: ActionWithPayload): OmsState {
	switch (action.type) {
		case omsActionType.UPDATE_MESSAGE:
		case omsActionType.UPDATE_MESSAGES: {
			return updateState(state, {
				...notificationsReducer(state, action, NOTIFICATION_STATUS_DICTIONARY),
			});
		}
		case omsActionType.GET_NOTIFICATIONS_BY_STATUS:
		case omsActionType.UPDATE_USER_SETTINGS: {
			return updateState(state, {
				isBusy: true,
			});
		}
		case omsActionType.GET_NOTIFICATIONS_BY_STATUS_SUCCESS: {
			const payload = action.payload as {
				status: OmsMessageStatus;
				notifications: OmsMessage[];
			};
			return updateState(state, {
				isBusy: false,
				notificationsByStatus: {
					...state.notificationsByStatus,
					[payload.status]: payload.notifications,
				},
			});
		}
		case omsActionType.GET_ALERTS_SUCCESS: {
			const alerts = action.payload as OmsMessage[];

			if (isEqual(state.alerts, alerts)) {
				return state;
			}

			return updateState(state, {
				alerts,
			});
		}
		case omsActionType.SET_ALERTS_CONTEXT: {
			return updateState(state, {
				alertContext: action.payload,
			});
		}
		case omsActionType.GET_USER_SETTINGS_SUCCESS: {
			return updateState(state, {
				userSettings: action.payload,
			});
		}
		case omsActionType.GET_NOTIFICATIONS_INFO_SUCCESS: {
			const notificationsInfo = action.payload as NotificationsInfo;

			if (isEqual(state.notificationsInfo, notificationsInfo)) {
				return state;
			}

			return updateState(state, {
				notificationsInfo,
			});
		}
		case omsActionType.UPDATE_USER_SETTINGS_SUCCESS: {
			return updateState(state, {
				isBusy: false,
				userSettings: action.payload,
			});
		}
		case omsActionType.GET_NOTIFICATIONS_BY_STATUS_ERROR:
		case omsActionType.UPDATE_MESSAGE_SUCCESS:
		case omsActionType.UPDATE_MESSAGE_ERROR:
		case omsActionType.UPDATE_MESSAGES_SUCCESS:
		case omsActionType.UPDATE_MESSAGES_ERROR:
		case omsActionType.UPDATE_USER_SETTINGS_ERROR: {
			return updateState(state, {
				isBusy: false,
			});
		}
		case omsActionType.ENABLE_REALTIME_ALERTS: {
			return updateState(state, {
				isAlertRealtimeEnabled: true,
			});
		}
		case omsActionType.DISABLE_REALTIME_ALERTS: {
			return updateState(state, {
				isAlertRealtimeEnabled: false,
			});
		}
		case omsActionType.ENABLE_REALTIME_NOTIFICATIONS_INFO: {
			return updateState(state, {
				isNotificationInfoRealtimeEnabled: true,
			});
		}
		case omsActionType.DISABLE_REALTIME_NOTIFICATIONS_INFO: {
			return updateState(state, {
				isNotificationInfoRealtimeEnabled: false,
			});
		}
		case omsActionType.RESET: {
			return initialState;
		}
		default:
			return state;
	}
}

function notificationsReducer(
	state = initialState,
	action: ActionWithPayload<UpdateMessagePayload | UpdateMessagesPayload>,
	notificationStateDictionary: NotificationStatusDictionary
): NotificationsState {
	const modifiedMessages = "messages" in action.payload ? action.payload.messages : [action.payload.message];

	return {
		notificationsByStatus: {
			...state.notificationsByStatus,
			[action.payload.status]: selectAndModifyNotifications(
				state.notificationsByStatus[action.payload.status],
				modifiedMessages,
				notificationStateDictionary
			),
		},
		notificationsInfo: {
			...state.notificationsInfo,
			unreadNotificationCount: updateUnreadNotificationCount(
				state.notificationsInfo.unreadNotificationCount,
				modifiedMessages
			),
		},
	};
}

function selectAndModifyNotifications(
	notifications: OmsMessage[],
	modifiedMessages: UpdateMessage[],
	notificationStateDictionary: NotificationStatusDictionary
): OmsMessage[] {
	const cloneNotifications = [...notifications];

	modifiedMessages.forEach((message) => {
		const status = notificationStateDictionary[message.action];

		if (!status) {
			return;
		}

		const notificationIndex = cloneNotifications.findIndex((notification) => message.messageId === notification.id);
		if (notificationIndex > -1) {
			const updatedNotification = { ...cloneNotifications[notificationIndex], ...status };
			cloneNotifications.splice(notificationIndex, 1, updatedNotification);
		}
	});

	return cloneNotifications;
}

function updateUnreadNotificationCount(originalNotificationCount: number, modifiedMessages: UpdateMessage[]) {
	const readedNotification = modifiedMessages.filter((message) => message.action === OmsMessageAction.Read).length;
	return originalNotificationCount - readedNotification;
}
